/**
 * Product Name Helper Utilities
 *
 * Centralized logic for handling product name display with fallback patterns.
 */

/**
 * Minimal product shape required for display name logic
 */
export type ProductNameFields = {
  product_cas_name: string;
  preferred_product_name?: string | null;
};

/**
 * Get the display name for a product, prioritizing manufacturer-provided name
 * over CAS-derived name.
 *
 * @param product - Product object or partial with name fields
 * @returns The preferred product name if available, otherwise the CAS name
 *
 * @example
 * ```typescript
 * const product = {
 *   product_cas_name: 'Acetylsalicylic acid',
 *   preferred_product_name: 'Aspirin'
 * };
 * getProductDisplayName(product); // Returns: 'Aspirin'
 * ```
 *
 * @example
 * ```typescript
 * const product = {
 *   product_cas_name: 'Acetylsalicylic acid',
 *   preferred_product_name: null
 * };
 * getProductDisplayName(product); // Returns: 'Acetylsalicylic acid'
 * ```
 */
export function getProductDisplayName(product: ProductNameFields): string {
  return product.preferred_product_name || product.product_cas_name;
}
